/**
 * @fileoverview Native browser credential storage.
 * @description "Remember me" is implemented via the browser's own password manager, not a custom
 * store — checking it just prompts the browser (via the Credential Management API) to offer
 * saving the identifier/password just used, the same "Save password?" UI a normal `<form>` login
 * would trigger. The browser then handles autofill/suggestion on a later visit entirely on its
 * own; this app doesn't need to read anything back. Chromium-based browsers only (Chrome, Edge) —
 * Firefox and Safari don't implement PasswordCredential, so this is a deliberate no-op there
 * rather than a custom fallback, since the point is the browser's native UI specifically.
 * PasswordCredential's own ambient type is declared locally below: TypeScript's DOM lib dropped
 * it (it's a non-standard/Chromium-only API now), even though `navigator.credentials.store()`
 * itself is still live and functional in current Chrome/Edge.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

interface PasswordCredentialData {
  id: string
  password: string
  name?: string
  iconURL?: string
}

declare global {
  interface PasswordCredential extends Credential {
    readonly password: string
    readonly name: string | null
    readonly iconURL: string | null
  }

  var PasswordCredential: {
    prototype: PasswordCredential
    new (data: PasswordCredentialData): PasswordCredential
  }
}

/**
 * @description Checks whether this browser supports the Credential Management API's password
 * storage.
 * @returns {boolean} True when `navigator.credentials.store` with `PasswordCredential` is usable.
 */
export function isCredentialStorageSupported(): boolean {
  return (
    typeof PasswordCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'credentials' in navigator
  )
}

/**
 * @description Prompts the browser to offer saving the just-used login credential. Best-effort
 * only — unsupported browsers and a rejected/dismissed prompt are both silently ignored, since
 * this should never block or interrupt sign-in.
 * @param {string} identifier - The identifier signed in with.
 * @param {string} password - The password signed in with.
 */
export async function storeLoginCredential(identifier: string, password: string): Promise<void> {
  if (!isCredentialStorageSupported()) {
    return
  }

  try {
    const credential = new PasswordCredential({ id: identifier, password, name: identifier })
    await navigator.credentials.store(credential)
  } catch {
    // Unsupported or dismissed — never block sign-in over this.
  }
}
